import React from 'react';
import {
  ArenaGameState,
  CardGameType,
  GamePhase,
  SeatPosition,
  Direction,
  TrumpSuit,
  BidCategory,
  BidRecord,
  TablePlayer
} from '../state/ArenaTypes';
import { GameTableConfig } from '../engine/tableConfig/types';

/**
 * Standard game event item describing authoritative facts produced by game rules.
 */
export interface GameEventItem {
  id: string;
  type: string;
  payload?: any;
  timestamp?: number;
}

/**
 * Universal props contract for any game's top-level or table HUD component.
 */
export interface GameHudProps {
  gameState?: ArenaGameState;
  tableColor?: string;
  isPlayPhase?: boolean;
  mySeat?: SeatPosition | null;
  config?: GameTableConfig;
  winningContractSummary?: any;
  isDelayingWinningContract?: boolean;
  booksNeededToSet?: number | null;
  booksToSetData?: any;
  showBooksToSet?: boolean;
  cardGameType?: string;
  team1Bid?: number;
  team2Bid?: number;
  team1Books?: number;
  team2Books?: number;
  team1Bags?: number;
  team2Bags?: number;
  spadesBroken?: boolean;
  heartsBroken?: boolean;
  passingDirection?: string;
  seatPoints?: number[];
  [key: string]: any;
}

/**
 * Universal props contract for any game's phase overlay component.
 */
export interface GamePhaseProps {
  gameState: ArenaGameState;
  mySeat?: SeatPosition | null;
  isActionPending?: boolean;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  onSubmitBid?: (bid: number, direction: Direction | undefined, category: BidCategory) => void;
  onPass?: () => void;
  onDeclareTrumpSuit?: (suit: TrumpSuit) => void;
  onDeclareNoTrumpDirection?: (direction: Direction) => void;
  onConfirmDiscards?: (cardIds: string[]) => void;
  onConfirmPass?: (cardIds: string[]) => void;
  config?: GameTableConfig;
  [key: string]: any;
}

/**
 * Universal props contract for game-specific interactive action overlays.
 */
export interface GameActionProps {
  gameState: ArenaGameState;
  action?: any;
  mySeat?: SeatPosition | null;
  [key: string]: any;
}

/**
 * Universal props contract for game-specific event visual effect / animation components.
 */
export interface GameEventProps {
  event: GameEventItem;
  gameState?: ArenaGameState;
  isPlayPhase?: boolean;
  [key: string]: any;
}

/**
 * Universal props contract for game-specific hand-end celebratory / sadness pills.
 */
export interface CelebrationPillProps {
  gameState: ArenaGameState;
  mySeat: number | null;
  onDismiss: () => void;
  canContinue?: boolean;
  bostonResult?: import('../games/common/IGameRules').CelebrationResult | null;
  isBiddingTeamSet?: boolean;
  isUserTeamSet?: boolean | null;
  isClaimRevealActive?: boolean;
  isUserTeamBidder?: boolean | null;
}

/**
 * Universal props contract for game-specific full-screen celebratory overlays/animations.
 */
export interface CelebrationOverlayProps {
  isOpen: boolean;
  isUserTeam?: boolean;
  winningTeam?: number;
  gameState?: ArenaGameState;
  mySeat?: number | null;
  bostonResult?: import('../games/common/IGameRules').CelebrationResult | null;
  onDismiss?: () => void;
}

/**
 * Authoritative Game Presentation definition.
 * Each game registers its own HUD, celebratory pill, celebration overlay, phase overlays, action handlers, and event visual effects.
 */
export interface GamePresentation {
  Hud: React.ComponentType<GameHudProps>;

  CelebrationPill?: React.ComponentType<CelebrationPillProps>;

  CelebrationOverlay?: React.ComponentType<CelebrationOverlayProps>;

  phases?: Partial<
    Record<GamePhase | string, React.ComponentType<GamePhaseProps | any>>
  >;

  actions?: Partial<
    Record<string, React.ComponentType<GameActionProps | any>>
  >;

  events?: Partial<
    Record<string, React.ComponentType<GameEventProps | any>>
  >;
}

const EmptyHud: React.FC<GameHudProps> = () => null;

/**
 * Fallback presentation for unknown, missing, or unsupported games.
 * Guarantees that an unsupported or unconfigured game renders nothing
 * instead of silently masquerading as Bid Whist.
 */
export const UNSUPPORTED_PRESENTATION: GamePresentation = {
  Hud: EmptyHud,
  phases: {},
  events: {},
};

